/**
 * Discord writes a guild emoji into message content as `<:name:id>`, or
 * `<a:name:id>` when it is animated. A sign-up position stores exactly that
 * string, so one value renders in a Discord embed *and* here — the alternative
 * was a second column the two sides could drift apart on.
 *
 * The web cannot render that markup, so it is parsed back into a CDN url.
 */
export interface CustomEmoji {
  kind: 'custom'
  id: string
  name: string
  animated: boolean
  url: string
}

export interface UnicodeEmoji {
  kind: 'unicode'
  char: string
}

export type ParsedEmoji = CustomEmoji | UnicodeEmoji

const CUSTOM_RE = /^<(a?):([A-Za-z0-9_]{2,32}):(\d{15,25})>$/

export function parseEmoji(raw?: string | null): ParsedEmoji | null {
  const value = (raw ?? '').trim()
  if (!value) return null
  const m = CUSTOM_RE.exec(value)
  if (!m) return { kind: 'unicode', char: value }
  const animated = m[1] === 'a'
  return {
    kind: 'custom',
    id: m[3],
    name: m[2],
    animated,
    url: emojiUrl(m[3], animated),
  }
}

export function emojiUrl(id: string, animated = false): string {
  return `https://cdn.discordapp.com/emojis/${id}.${animated ? 'gif' : 'webp'}?size=64`
}

/** One row of `GET /api/guild/emojis`. */
export interface GuildEmoji {
  id: string
  name: string
  animated: boolean
  /** The `<:name:id>` form — this is what gets stored. */
  text: string
  url: string
}

let guildEmojiCache: Promise<GuildEmoji[]> | null = null

/**
 * The server's custom emojis, fetched once per page. The picker is opened over
 * and over while a lineup is set up; the list behind it barely changes.
 */
export function fetchGuildEmojis(): Promise<GuildEmoji[]> {
  if (!guildEmojiCache) {
    guildEmojiCache = fetch('/api/guild/emojis', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : []))
      .then((rows: GuildEmoji[]) => (Array.isArray(rows) ? rows : []))
      .catch(() => {
        guildEmojiCache = null   // don't get stuck on one failed load
        return []
      })
  }
  return guildEmojiCache
}
